import {
  Box,
  CircularProgress,
  Link,
  linkClasses,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { PublicButton } from 'public/components/common/button/button';
import { theme } from 'redesign/theme';
import { IdScanManualStepProps } from './types';
import { useIdScanManualStep } from './hooks/use-id-scan-manual-step';
import { FormTermsAndConditionsCheck } from '../../../../common/forms/form-terms-and-conditions-check';
import { RearIdScanCard } from '../../../id-card-placeholders/rear-id-scan-card/rear-id-scan-card';
import { FrontIdScanCard } from '../../../id-card-placeholders/front-id-scan-card/front-id-scan-card';
import './index.css';

export const IdScanManualStep = ({ onSuccess, isSubmitting }: IdScanManualStepProps) => {
  const { t } = useTranslation('web-common');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    formState: { isValid },
  } = useFormContext();
  const { elementRef, status, activeSide, front, rear, startCapture } =
    useIdScanManualStep(onSuccess);

  if (status === 'loading-sdk') {
    return (
      <CenteredBox>
        <CircularProgress size={32} />
        <StyledLoadingText>{t('id-submission.loading-document-scanner.message')}</StyledLoadingText>
      </CenteredBox>
    );
  }

  const isCapturing = activeSide !== null;

  return (
    <Wrapper>
      {!isCapturing ? (
        <>
          <CardContainer>
            <FrontIdScanCard onClick={() => startCapture('front')} image={front} />
            <RearIdScanCard onClick={() => startCapture('rear')} image={rear} />
          </CardContainer>

          <StyledButtonContainer>
            <FormTermsAndConditionsCheck name="termsAndConditionsAccepted">
              <Trans
                i18nKey="upload-certificate-screen.terms-and-conditions.agreement"
                components={[
                  <StyledLink
                    href={t('upload-certificate-screen.terms-and-conditions.link')}
                    target="_blank"
                    key="terms-and-conditions-link"
                  />,
                ]}
                ns="web-common"
              ></Trans>
            </FormTermsAndConditionsCheck>
            <PublicButton
              variant="contained"
              type="submit"
              fullWidth={isMobile}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={20} />
              ) : (
                t('id-submission.submit-button.label')
              )}
            </PublicButton>
          </StyledButtonContainer>
        </>
      ) : (
        <StyledDocumentReaderContainer>
          <document-reader ref={elementRef} />
        </StyledDocumentReaderContainer>
      )}
    </Wrapper>
  );
};

const CenteredBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(12, 0),
}));

const StyledLoadingText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const Wrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const CardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(12),

  [theme.breakpoints.down('md')]: {
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(4),
    gap: theme.spacing(4),
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  [`&.${linkClasses.root}`]: {
    color: theme.palette.lwBlack[100],
    textDecoration: 'underline',
    fontVariationSettings: '"wght" 600',
  },
}));

const StyledButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(12, 0),

  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(6),
  },
}));

const StyledDocumentReaderContainer = styled(Box)(({ theme }) => ({
  width: '600px',
  height: '100%',

  [theme.breakpoints.down('md')]: {
    height: '100%',
    width: '100vw',
  },
}));
